import useBackendClient from "@/client";
import { IAP, IAPStoreItem, IAPWithStoreItem } from "@/modules/IAP/types";
import { ref } from "vue";
import useToast from "@/hooks/useToastNotification";

const IAPs = ref<IAPWithStoreItem[]>([]);

export type CreateGooglePlayIAPRequest = {
  iapName: string;
  iapDescription: string;
  productId: string;
  pricingTemplateId: string;
};

export default function useIAPs() {
  const { client } = useBackendClient();
  const { toastError } = useToast();

  async function getIAPs(appId: string, versionId: string, platform: string) {
    try {
      const { data } = await client.get<IAP[]>(
        `/app/${appId}/iaps?countryIso=USA&type=CONSUMABLE,NON_CONSUMABLE&onlyOriginalIAPs=false&platform=${platform}`
      );
      IAPs.value = await Promise.all(
        data.map(async (iap) => {
          const storeItemsResponse = await client.get<IAPStoreItem[]>(
            `/app/${appId}/version/${versionId}/sku-mapping/${iap.id}`
          );
          return {
            ...iap,
            storeItems: await Promise.all(
              storeItemsResponse.data.map(async (storeItem) => {
                const storeItemResponse = await client.get(
                  `/app/${appId}/version/${versionId}/store-items/${storeItem.storeItemId}`
                );
                return {
                  ...storeItem,
                  storeItemName: storeItemResponse.data.name,
                };
              })
            ),
          };
        })
      );
    } catch (error) {
      toastError({ detail: "An error occurred while fetching IAPs.", summary: "Error", life: 3000 });
    }
  }

  async function createGooglePlayIAP(appId: string, iapData: CreateGooglePlayIAPRequest) {
    try {
      return await client.post(`/app/${appId}/iaps/createGooglePlayIAP`, iapData);
    } catch (error) {
      toastError({
        detail: "An error occurred while creating IAP.",
        summary: "Error",
      });
      return null;
    }
  }

  async function syncGooglePlayIAP(appId: string, iapId: string) {
    try {
      return await client.put(`/app/${appId}/iaps/${iapId}/sync`);
    } catch (error) {
      toastError({
        detail: "An error occurred while syncing IAP.",
        summary: "Error",
      });
      return null;
    }
  }

  return { getIAPs, IAPs, createGooglePlayIAP, syncGooglePlayIAP };
}
